/**
 * AP-32 Nachtrag 4 — **Lieferadresse der Bestellung, Zustelladresse des Lieferscheins.**
 *
 * Frei getippt oder übernommen: aus den Adressen eines beliebigen
 * Geschäftspartners (die Ware geht an die Baustelle des Kunden) oder aus der
 * eigenen Firmenanschrift. Gedruckt wird die Adresse nur auf der Bestellung bzw.
 * auf dem Lieferschein, der mit der Ware reist.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { DocumentKind } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { documentsApi } from '../api/documents.api'
import { PartnerPicker } from './PartnerPicker'
import type { PartnerAddressRef } from '../types/document.types'

interface Props {
  kind: DocumentKind
  value: string
  onChange: (wert: string) => void
  editierbar: boolean
  /** Der Partner des Belegs — beim Lieferschein der erste Vorschlag. */
  partner: { id: string | null; name: string }
  eingabe: string
  label: string
}

export function DeliveryAddressField({ kind, value, onChange, editierbar, partner, eingabe, label }: Props) {
  const { t } = useTranslation()
  const lieferschein = kind === 'DELIVERY_NOTE'
  const [offen, setOffen] = useState(false)
  const [quelle, setQuelle] = useState<{ id: string | null; name: string }>(lieferschein ? partner : { id: null, name: '' })
  const [adressen, setAdressen] = useState<PartnerAddressRef[]>([])
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    if (!offen || !quelle.id) {
      setAdressen([])
      return
    }
    setFehler(null)
    documentsApi
      .partnerAddresses(quelle.id)
      .then(({ data }) => setAdressen(data.data))
      .catch((err) => setFehler(fehlertext(err, t)))
  }, [offen, quelle.id, t])

  const eigene = async () => {
    setFehler(null)
    try {
      const { data } = await documentsApi.ownAddress()
      onChange(data.data.lines.join('\n'))
      setOffen(false)
    } catch (err) {
      setFehler(fehlertext(err, t))
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className={label}>{t(lieferschein ? 'documents.head.shippingAddress' : 'documents.head.deliveryAddress')}</label>
        {editierbar && (
          <button type="button" onClick={() => setOffen((o) => !o)} className="mb-1 text-xs text-primary hover:underline">
            {t('documents.head.pickAddress')}
          </button>
        )}
      </div>
      <textarea
        disabled={!editierbar}
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={eingabe}
        placeholder={t('documents.head.deliveryAddressPlaceholder')}
      />
      <p className="mt-1 text-xs text-muted-foreground">
        {t(lieferschein ? 'documents.head.shippingAddressHint' : 'documents.head.deliveryAddressHint')}
      </p>
      {offen && editierbar && (
        <div className="mt-2 space-y-2 rounded-md border border-border p-2">
          <PartnerPicker value={quelle} onSelect={(p) => setQuelle({ id: p.id, name: p.name })} />
          {fehler && <div className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{fehler}</div>}
          {quelle.id && (
            <ul className="space-y-1">
              {adressen.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(a.lines.join('\n'))
                      setOffen(false)
                    }}
                    className="w-full rounded px-2 py-1 text-left text-xs hover:bg-accent"
                  >
                    <span className="font-medium">
                      {a.label || t(`documents.head.addressTypes.${a.type}`)}
                      {a.isDefault ? ` · ${t('documents.head.addressDefault')}` : ''}
                    </span>
                    <span className="block text-muted-foreground">{a.lines.join(', ')}</span>
                  </button>
                </li>
              ))}
              {adressen.length === 0 && !fehler && <li className="px-2 text-xs text-muted-foreground">{t('documents.head.noAddresses')}</li>}
            </ul>
          )}
          <div className="flex flex-wrap gap-3 text-xs">
            <button type="button" onClick={() => void eigene()} className="text-primary hover:underline">
              {t('documents.head.ownAddress')}
            </button>
            {value && (
              <button type="button" onClick={() => onChange('')} className="text-muted-foreground hover:underline">
                {t('documents.head.clearAddress')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
